import React, { Suspense } from "react";
import Button from "../../../components/common/button/button";
import Card from "../../../components/common/card/card";
import { StoreIcon, UpArrowCardIcon, DownArrowCardIcon, CheckmarkIcon, MailIcon } from "../../../assets/Icons";
import { IIconBasicProps } from "../../../assets/Icons/IIconBasicProps";
import { ExpertReviewWarningCard, ItemCard } from "../ItemCard";
import { formatEstimate, getExpertReviewMessage, isJewelryItem } from "../ez-quote.helpers";
import { Item, StoreAddress, StoreOption } from "../ez-quote.types";
import { useTranslation } from "../../../contexts/TranslationContext";

interface FinalReviewStepProps {
  items: Item[];
  quoteId: string | null;
  totalEstimate: { min: number; max: number };
  error: string | null;
  smsDeliveryStatus: string | null;
  nearbyStores: StoreOption[];
  zipCodeFromModal: string | null;
  brand: string | null;
  openExpertReviewAccordion: boolean;
  setOpenExpertReviewAccordion: (v: boolean) => void;
  openEstimateCard: boolean;
  setopenEstimateCard: (v: boolean) => void;
  emailSendCount: { [quoteId: string]: number };
  setOtpModalMode: (mode: "phone" | "email") => void;
  setIsOtpModalVisible: (v: boolean) => void;
  scrollToElement: (element: HTMLElement | null) => boolean;
  quoteDetailsRef: React.RefObject<HTMLDivElement | null>;
  resetForm: () => void;
  storeAddress: StoreAddress | null;
  setIsQuoteModalVisible: (v: boolean) => void;
  getIconComponent: (name: string | null) => React.FC<IIconBasicProps>;
}

const FinalReviewStep: React.FC<FinalReviewStepProps> = ({
  items,
  quoteId,
  totalEstimate,
  error,
  nearbyStores,
  zipCodeFromModal,
  brand,
  openExpertReviewAccordion,
  setOpenExpertReviewAccordion,
  openEstimateCard,
  setopenEstimateCard,
  emailSendCount,
  setOtpModalMode,
  setIsOtpModalVisible,
  smsDeliveryStatus,
  scrollToElement,
  quoteDetailsRef,
  resetForm,
  storeAddress: _storeAddress,
  setIsQuoteModalVisible,
  getIconComponent,
}) => {
  const t = useTranslation();

  const getQuoteGenerationStatus = () => {
    if (error) {
      return "FAILURE";
    }

    if (items.length === 0) {
      return "FAILURE";
    }

    const hasNAQuotes = items.some((item) => item.estimate === "N/A");
    const hasValidQuotes = items.some((item) => item.estimate !== "N/A");

    if (hasNAQuotes && !hasValidQuotes) {
      return "UNAVAILABLE";
    }

    if (hasNAQuotes && hasValidQuotes) {
      return "PARTIAL_SUCCESS";
    }

    return "SUCCESS";
  };

  const hasNAQuotes = items.some((item) => item.estimate === "N/A");
  const hasGoldItems = items.some(isJewelryItem);
  // Diamond disclaimer explains what a jewelry *estimate* excludes, so it only
  // applies to priced jewelry items (LW-1536: an unpriced GM item alongside a
  // priced diamond item shows both banners).
  const itemsWithDiamond = items.filter((item) => {
    const isPricedJewelryItem = isJewelryItem(item) && item.estimate !== "N/A";
    if (!isPricedJewelryItem) return false;
    const diamondsEntry = Object.entries(item.attributes).find(([key]) =>
      key.toLowerCase().includes("diamond")
    );
    return diamondsEntry ? diamondsEntry[1].id?.toLowerCase() === "yes" : false;
  });
  const hasItemsWithDiamond = itemsWithDiamond.length > 0;
  const diamondItemMetalNames = [...new Set(itemsWithDiamond.map((item) => item.goldTypeName ?? "Gold"))];
  const diamondMetalLabel = diamondItemMetalNames.length === 1
    ? diamondItemMetalNames[0].toLowerCase()
    : "metal";
  const someItemsHaveValidQuotes =
    items.length > 0 && items.some((item) => item.estimate !== "N/A");
  const showWhyInstoreAccordion = hasGoldItems || hasNAQuotes;
  const showEstimatedRange =
    someItemsHaveValidQuotes && (totalEstimate.min || totalEstimate.max);

  return (
    <div
      className="final-review-container"
      data-quote-generation-status={getQuoteGenerationStatus()}
      data-sms-delivery-status={smsDeliveryStatus ?? undefined}
    >
      <div className="total-estimate-container">
        <div className="total-estimate-quote">{t.finalReview.totalEstimate}</div>
        <div className="quote-id">{quoteId ? t.finalReview.quoteId(quoteId) : null}</div>
        {showEstimatedRange && (
          <>
            <div className="range">
              ${formatEstimate(totalEstimate.min)} - ${formatEstimate(totalEstimate.max)}
            </div>
            <a
              href="#"
              className="info-link"
              onClick={() => setIsQuoteModalVisible(true)}
            >
              {t.finalReview.howDerived}
            </a>
          </>
        )}

        {/* Expert Review Warning (unpriced items) */}
        {hasNAQuotes && (
          <ExpertReviewWarningCard
            message={getExpertReviewMessage(items, t)}
            iconSize={20}
            className="expert-review-warning-card-final"
          />
        )}

        {/* Diamond disclaimer — only when a priced gold item has diamonds = yes.
            Follows the unpriced-items banner above (LW-1536 GM + Jewelry). */}
        {hasItemsWithDiamond && (
          <ExpertReviewWarningCard
            message={t.finalReview.expertReviewNeeded}
            bullets={[
              t.finalReview.diamondNote(diamondMetalLabel),
              t.finalReview.conditionNote,
            ]}
            iconSize={20}
            className="expert-review-warning-card-final gold-warning-card"
          />
        )}

        {/* Why Expert Review Accordion */}
        {showWhyInstoreAccordion && (
          <div className="expert-review-accordion">
            <Card
              title={t.finalReview.whyInstore}
              Icon={() => null}
              onClick={() => setOpenExpertReviewAccordion(!openExpertReviewAccordion)}
              className={`expert-review-accordion-header ${
                openExpertReviewAccordion ? "expanded" : ""
              }`}
              navigation={true}
              NavigationIcon={() =>
                openExpertReviewAccordion ? <UpArrowCardIcon /> : <DownArrowCardIcon />
              }
            />
            {openExpertReviewAccordion && (
              <div className="accordion-content">
                <ul className="expert-benefits">
                  <li>
                    <div className="benefit-icon">
                      <Suspense fallback={null}>
                        <CheckmarkIcon width={15} height={11} />
                      </Suspense>
                    </div>
                    <span>{t.finalReview.modelsMissing}</span>
                  </li>
                  <li>
                    <div className="benefit-icon">
                      <Suspense fallback={null}>
                        <CheckmarkIcon width={15} height={11} />
                      </Suspense>
                    </div>
                    <span>{t.finalReview.authenticity}</span>
                  </li>
                  <li>
                    <div className="benefit-icon">
                      <Suspense fallback={null}>
                        <CheckmarkIcon width={15} height={11} />
                      </Suspense>
                    </div>
                    <span>{t.finalReview.premiumMaterials}</span>
                  </li>
                </ul>
              </div>
            )}
          </div>
        )}
        <div className="email-copy-button">
         <Button
           label={t.finalReview.emailCopy}
           onClick={() => {
             setOtpModalMode("email");
             setIsOtpModalVisible(true);
           }}
           styleType="secondary"
           icon={
             <Suspense fallback={null}>
               <MailIcon key={brand || 'default'} />
             </Suspense>
           }
           disabled={quoteId ? (emailSendCount[quoteId] || 0) >= 2 : false}
         />
         {quoteId && (emailSendCount[quoteId] || 0) >= 2 && (
           <p className="email-limit-message">{t.finalReview.emailLimit}</p>
         )}
       </div>
         <div className="quote-details-button">
          <Button
            label={t.finalReview.quoteDetailsBtn}
            onClick={() => {
              scrollToElement(quoteDetailsRef.current);
            }}
            styleType="secondary"
          />
        </div>
      </div>


      {/* Store Information Section */}
      <div className="total-estimate-container">
        {nearbyStores.length > 0 && (
          <div className="store-section">
            <div className="store-section-title">
              {t.finalReview.bringItems}
            </div>
            <div className="store-section-subtitle">
              {zipCodeFromModal ? t.finalReview.storesNear(zipCodeFromModal) : t.finalReview.storeClosestToYou}
            </div>
            <div className="store-list">
              {nearbyStores.slice(0, 3).map((store) => (
                <div key={store.key} className="store-card">
                  <div className="store-info">
                    <div className="store-icon">
                      <Suspense fallback={null}>
                        <StoreIcon key={brand || 'default'} width={24} height={24} />
                      </Suspense>
                    </div>
                    <div className="store-details">
                    <div className="store-name">{store.brandName}</div>
                      <div className="store-address">{store.address}</div>
                      {store.phoneNumber && (
                        <div
                          className="store-phone"
                          onClick={() => {
                            window.location.href = `tel:${store.phoneNumber}`;
                          }}
                        >
                          {store.phoneNumber}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="store-actions">
                    <Button
                      label={t.finalReview.getDirections}
                      onClick={() => {
                        const encodedAddress = encodeURIComponent(
                          store.address
                        );
                        const mapUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodedAddress}`;
                        window.open(mapUrl, "_blank");
                      }}
                      styleType="outline"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="find-another-store-button">
          <Button
            label={t.finalReview.findAnotherStore}
            onClick={() => {
              window.open("https://ezpawn.com/find-a-location/", "_blank");
            }}
            styleType="secondary"
          />
        </div>
      </div>
      <div className="quote-details" ref={quoteDetailsRef}>{t.finalReview.quoteDetailsHeading}</div>
      {items.map((item) => (
        <ItemCard
          key={item.id}
          item={item}
          getIconComponent={getIconComponent}
          isFinalReview={true}
          setEstimateCard={() => setopenEstimateCard(!openEstimateCard)}
          openEstimateCardFlag={openEstimateCard}
        />
      ))}
      <div className="start-newquote-button">
        <Button
          label={t.finalReview.startNewQuote}
          onClick={() => resetForm()}
          styleType="secondary"
        />
      </div>
    </div>
  );
};

export default FinalReviewStep;
